import L from "leaflet";
import "leaflet/dist/leaflet.css";
import osm2geojson from "osm2geojson-lite";
import { useCallback, useEffect, useState } from "react";
import { GeoJSON, MapContainer, TileLayer } from "react-leaflet";
import MapController from "../components/map/MapController";
import MapSidebar from "../components/map/MapSidebar";
import SystemMarkers from "../components/map/SiteMarkers";
import Navbar from "../components/navigation/Navbar";
import { useRegions } from "../context/RegionsContext";
import { useMapData } from "../hooks/useMapData";
import { configureLeafletIcons, createSafeFitBounds } from "../utils/mapUtils";

// Configure Leaflet icons once
configureLeafletIcons();

function MapView() {
  const { regionsContext, regions, allSystems, isLoading, error } =
    useRegions();

  const { isLoadingBoundary, resetMap, handleCheckboxChange } =
    useMapData(regionsContext);

  const [currentRegion, setCurrentRegion] = useState(null);
  const [cityBoundary, setCityBoundary] = useState(null);
  const [geoJson, setGeoJson] = useState(null);
  const [selectedSystem, setSelectedSystem] = useState(null);
  const [selectedSystemIds, setSelectedSystemIds] = useState(new Set());
  const [mapCenter, setMapCenter] = useState({ lat: 42.3601, lng: -71.0589 });
  const [zoomLevel, setZoomLevel] = useState(13);
  const [mapInstance, setMapInstance] = useState(null);
  const [showRegionDropdown, setShowRegionDropdown] = useState(false);

  // NEW: Get selectedRegion from context
  const { selectedRegion } = useRegions();

  // NEW: Sync map region with navbar selection
  useEffect(() => {
    if (selectedRegion && selectedRegion !== currentRegion) {
      handleRegionChange(selectedRegion);
    }
  }, [selectedRegion, currentRegion]);

  // Initialize with first region when data is loaded
  useEffect(() => {
    if (!isLoading && regions.length > 0 && !currentRegion) {
      const firstRegion =
        regions.find((r) => r.region_id !== "all") ||
        regions[0];
      setCurrentRegion(firstRegion);

      if (firstRegion && firstRegion.region_id !== "all") {
        const lat = parseFloat(firstRegion.region_center_latitude);
        const lng = parseFloat(firstRegion.region_center_longitude);
        if (!isNaN(lat) && !isNaN(lng)) {
          setMapCenter({ lat, lng });
        }
      }
    }
  }, [regions, isLoading, currentRegion]);

  // Load boundary data when region is set
  useEffect(() => {
    async function loadBoundary() {
      if (
        !currentRegion ||
        currentRegion.region_id === "all"
      ) {
        setCityBoundary(null);
        setGeoJson(null);
        return;
      }

      if (currentRegion?.region_boundary) {
        try {
          const relId = currentRegion.region_boundary;
          const url = `https://overpass-api.de/api/interpreter?data=[out:json];relation(${relId});out geom;`;

          const osmRes = await fetch(url);
          const osmData = await osmRes.json();
          const converted = osm2geojson(osmData);
          setGeoJson(converted);

          if (
            osmData.elements &&
            osmData.elements[0] &&
            osmData.elements[0].bounds
          ) {
            setCityBoundary(osmData.elements[0].bounds);
          }
        } catch (err) {
          console.error("Error loading boundary for region:", err);
          setCityBoundary(null);
          setGeoJson(null);
        }
      }
    }

    loadBoundary();
  }, [currentRegion]);

  const handleSystemClick = (system) => {
    setSelectedSystem(system);
    const lat = parseFloat(system.system_latitude);
    const lng = parseFloat(system.system_longitude);

    if (!isNaN(lat) && !isNaN(lng)) {
      setMapCenter({ lat, lng });
      setZoomLevel(20);
    }

    setSelectedSystemIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(system.system_id)) {
        newSet.delete(system.system_id);
      } else {
        newSet.add(system.system_id);
      }
      return newSet;
    });
  };

  const handleRegionChange = useCallback((region) => {
    setCurrentRegion(region);
    setShowRegionDropdown(false);
    setSelectedSystemIds(new Set());
    setSelectedSystem(null);

    //  CLEAR BOUNDARIES IMMEDIATELY
    setGeoJson(null);
    setCityBoundary(null);

    if (region && region.region_id !== "all") {
      const lat = parseFloat(region.region_center_latitude);
      const lng = parseFloat(region.region_center_longitude);
      if (!isNaN(lat) && !isNaN(lng)) {
        setMapCenter({ lat, lng });
        setZoomLevel(13);
      }
    }
  }, []);

  const safeFitBounds = useCallback(createSafeFitBounds(mapInstance), [
    mapInstance,
  ]);

  const fitToSelected = useCallback(() => {
    if (mapInstance && mapInstance._container && selectedSystemIds.size > 0) {
      const selectedSystems = allSystems.filter((s) =>
        selectedSystemIds.has(s.system_id),
      );
      const bounds = L.latLngBounds(
        selectedSystems.map((s) => [
          parseFloat(s.system_latitude),
          parseFloat(s.system_longitude),
        ]),
      );
      safeFitBounds(bounds);
    }
  }, [mapInstance, allSystems, selectedSystemIds, safeFitBounds]);

  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading map data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md">
          <div className="mb-4">
            <svg
              className="mx-auto h-12 w-12 text-red-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Access Error
          </h3>
          <p className="text-red-600 mb-4">{error}</p>
        </div>
      </div>
    );
  }

  if (!currentRegion) {
    return null;
  }

  // Calculate bounds with padding
  const bounds =
    cityBoundary &&
    !isNaN(parseFloat(cityBoundary.minlat)) &&
    !isNaN(parseFloat(cityBoundary.minlon)) &&
    !isNaN(parseFloat(cityBoundary.maxlat)) &&
    !isNaN(parseFloat(cityBoundary.maxlon))
      ? [
          [
            parseFloat(cityBoundary.minlat) - 0.05,
            parseFloat(cityBoundary.minlon) - 0.05,
          ],
          [
            parseFloat(cityBoundary.maxlat) + 0.05,
            parseFloat(cityBoundary.maxlon) + 0.05,
          ],
        ]
      : null;

  return (
    <>
      <Navbar />
      <div className="h-screen flex bg-gray-50 overflow-hidden pt-14">
        {/* Sidebar */}
        {/* <div className="w-80 bg-white shadow-lg overflow-y-auto">
          <div className="p-4 border-b">
            <div className="relative">
              <button
                onClick={() => setShowRegionDropdown(!showRegionDropdown)}
                className="w-full px-4 py-2 text-left bg-white border rounded-lg hover:bg-gray-50 flex items-center justify-between"
              >
                <span className="font-medium">
                  {currentMunicipalRegion?.municipal_region_name || 'Select Region'}
                </span>
                <svg
                  className={`w-5 h-5 transition-transform ${showRegionDropdown ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {showRegionDropdown && (
                <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {municipalRegions
                    .filter(r => r.municipal_region_id !== 'all')
                    .map((region) => (
                      <button
                        key={region.municipal_region_id}
                        onClick={() => handleRegionChange(region)}
                        className="w-full px-4 py-2 text-left hover:bg-gray-100 border-b last:border-b-0"
                      >
                        {region.municipal_region_name}
                      </button>
                    ))}
                </div>
              )}
            </div>
          </div>

          <div className="p-4">
            <h3 className="font-semibold mb-3">Sites ({allSites.length})</h3>
            <div className="space-y-2">
              {allSites.map((site) => (
                <button
                  key={site.composite_key}
                  onClick={() => handleSiteClick(site)}
                  className={`w-full p-3 text-left rounded-lg border transition-colors ${
                    selectedSiteIds.has(site.site_id)
                      ? 'bg-blue-50 border-blue-500'
                      : 'bg-white hover:bg-gray-50 border-gray-200'
                  }`}
                >
                  <div className="font-medium text-sm">{site.site_street}</div>
                  <div className="text-xs text-gray-500 mt-1">{site.site_type}</div>
                </button>
              ))}
            </div>
          </div>
        </div> */}

        <MapSidebar
          selectedRegion={currentRegion}
          municipalRegions={regions}
          showRegionDropdown={showRegionDropdown}
          onToggleDropdown={() => setShowRegionDropdown(!showRegionDropdown)}
          onSelectRegion={setCurrentRegion}
          onResetMap={resetMap}
          isLoadingBoundary={isLoadingBoundary}
          systems={allSystems}
          selectedSystemIds={selectedSystemIds}
          onSystemClick={handleSystemClick}
          onCheckboxChange={handleCheckboxChange}
          onFitToSelected={fitToSelected}
        />

        {/* Map */}
        <div className="flex-1 relative">
          <MapContainer
            center={[mapCenter.lat, mapCenter.lng]}
            zoom={zoomLevel}
            className="h-full w-full"
            maxBounds={bounds}
            maxBoundsViscosity={1.0}
            minZoom={10}
            zoomControl={true}
            style={{ overflow: "hidden" }}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution="&copy; OpenStreetMap contributors"
              maxZoom={22}
              maxNativeZoom={19}
            />

            {geoJson && (
              <GeoJSON
                key={`geojson-${currentRegion?.region_id}`}
                data={geoJson}
                style={{ color: "red", weight: 3, fillOpacity: 0 }}
              />
            )}

            <SystemMarkers systems={allSystems} onSystemClick={handleSystemClick} />

            <MapController
              center={mapCenter}
              zoom={zoomLevel}
              bounds={bounds}
              onMapReady={setMapInstance}
            />
          </MapContainer>
        </div>
      </div>
    </>
  );
}

export default MapView;
